import React, { useState, useEffect, useCallback } from 'react';
import { 
  Tabs, 
  Input, 
  Select, 
  Button, 
  Space, 
  Alert,
  Tooltip,
  Row,
  Col
} from 'antd';
import { 
  EyeOutlined, 
  EditOutlined, 
  CopyOutlined,
  DownloadOutlined,
  UploadOutlined
} from '@ant-design/icons';
import MDEditor from '@uiw/react-md-editor';
import TextPreview from './TextPreview';
import { 
  TEXT_TYPES, 
  detectTextType, 
  validateText,
  extractSummary
} from '../utils/textParser';

const { TextArea } = Input;
const { Option } = Select;

const RichTextEditor = ({ 
  value = '', 
  onChange, 
  placeholder = '请输入内容...',
  height = 400,
  showPreview = true,
  showToolbar = true,
  disabled = false,
  maxLength = 10000
}) => {
  const [content, setContent] = useState(value);
  const [textType, setTextType] = useState(TEXT_TYPES.PLAIN);
  const [activeTab, setActiveTab] = useState('edit');
  const [validation, setValidation] = useState({ isValid: true });
  const [autoDetect, setAutoDetect] = useState(true);

  // 同步外部值
  useEffect(() => {
    if (value !== content) {
      setContent(value);
      if (autoDetect) {
        const detected = detectTextType(value);
        setTextType(detected);
      }
    }
  }, [value, content, autoDetect]);

  // 验证内容
  useEffect(() => {
    if (content) {
      const result = validateText(content, textType);
      setValidation(result);
    } else {
      setValidation({ isValid: true });
    }
  }, [content, textType]);

  // 内容变化处理
  const handleContentChange = useCallback((newContent) => {
    setContent(newContent);
    
    if (autoDetect && newContent) {
      const detected = detectTextType(newContent);
      setTextType(detected);
    }
    
    onChange && onChange(newContent);
  }, [onChange, autoDetect]);

  // 手动切换文本类型
  const handleTypeChange = (newType) => {
    setTextType(newType);
    setAutoDetect(false);
  };

  // 重新启用自动检测
  const handleAutoDetect = () => {
    setAutoDetect(true);
    const detected = detectTextType(content);
    setTextType(detected);
  };

  // 复制内容到剪贴板
  const handleCopy = () => {
    navigator.clipboard.writeText(content).then(() => {
      // 这里可以添加成功提示
    });
  };

  // 导出内容
  const handleExport = () => {
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `content.${getFileExtension(textType)}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // 导入文件
  const handleImport = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.txt,.md,.html,.json';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const fileContent = e.target.result;
          handleContentChange(fileContent);
        };
        reader.readAsText(file);
      }
    };
    input.click();
  };

  // 获取文件扩展名
  const getFileExtension = (type) => {
    const extensions = {
      [TEXT_TYPES.PLAIN]: 'txt',
      [TEXT_TYPES.MARKDOWN]: 'md',
      [TEXT_TYPES.HTML]: 'html',
      [TEXT_TYPES.JSON]: 'json'
    };
    return extensions[type] || 'txt';
  };

  // 渲染编辑器
  const renderEditor = () => {
    if (textType === TEXT_TYPES.MARKDOWN && !disabled) {
      return (
        <MDEditor
          value={content}
          onChange={handleContentChange}
          height={height}
          preview="edit"
          hideToolbar={!showToolbar}
          data-color-mode="light"
        />
      );
    }

    return (
      <TextArea
        value={content}
        onChange={(e) => handleContentChange(e.target.value)}
        placeholder={placeholder}
        style={{ height: height, resize: 'vertical' }}
        disabled={disabled}
        maxLength={maxLength}
        showCount
      />
    );
  };

  // 渲染工具栏
  const renderToolbar = () => {
    if (!showToolbar) return null;

    return (
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Space>
            <Select
              value={textType}
              onChange={handleTypeChange}
              style={{ width: 120 }}
              size="small"
            >
              <Option value={TEXT_TYPES.PLAIN}>纯文本</Option>
              <Option value={TEXT_TYPES.MARKDOWN}>Markdown</Option>
              <Option value={TEXT_TYPES.HTML}>HTML</Option>
              <Option value={TEXT_TYPES.JSON}>JSON</Option>
            </Select>
            
            {!autoDetect && (
              <Button size="small" onClick={handleAutoDetect}>
                自动检测
              </Button>
            )}

            {validation.isValid && content && (
              <span style={{ fontSize: 12, color: '#666' }}>
                {extractSummary(content, 50)}
              </span>
            )}
          </Space>
        </Col>
        
        <Col>
          <Space>
            <Tooltip title="复制内容">
              <Button 
                size="small" 
                icon={<CopyOutlined />} 
                onClick={handleCopy}
                disabled={!content}
              />
            </Tooltip>
            
            <Tooltip title="导入文件">
              <Button 
                size="small" 
                icon={<UploadOutlined />} 
                onClick={handleImport}
                disabled={disabled}
              />
            </Tooltip>
            
            <Tooltip title="导出文件">
              <Button 
                size="small" 
                icon={<DownloadOutlined />} 
                onClick={handleExport}
                disabled={!content}
              />
            </Tooltip>
          </Space>
        </Col>
      </Row>
    );
  };

  if (showPreview) {
    return (
      <div>
        {renderToolbar()}
        
        {!validation.isValid && (
          <Alert
            message="内容格式错误"
            description={validation.error}
            type={validation.warning ? "warning" : "error"}
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab}
          items={[
            {
              key: 'edit',
              label: (
                <span>
                  <EditOutlined />
                  编辑
                </span>
              ),
              children: renderEditor()
            },
            {
              key: 'preview',
              label: (
                <span>
                  <EyeOutlined />
                  预览
                </span>
              ),
              children: (
                <TextPreview 
                  content={content} 
                  title="" 
                  maxHeight={height}
                  showTypeTag={false}
                />
              )
            }
          ]}
        />
      </div>
    );
  }

  return (
    <div>
      {renderToolbar()}
      
      {!validation.isValid && (
        <Alert
          message="内容格式错误"
          description={validation.error}
          type={validation.warning ? "warning" : "error"}
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      
      {renderEditor()}
    </div>
  );
};

export default RichTextEditor; 